/* DCG V3.1.1 · 20 OCT 2026 · hotfix.1, H9 (docs/REVIEW-engineering-V3-1-0.md): the guard applies its state again after a pick of another condition (a change on another select of the product section), not only on a native change of the master select, which theme.js never fires */
(() => {
  const SECTION_SELECTOR = '[data-section-type="product"], [data-section-type="product-template"]';
  const INIT_FLAG = 'dcgQtyGuardInit';

  const WORD_TO_NUM = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10
  };

  function parseLimitFromTags(tags) {
    // Prefer the shared parser (dcg-utils.js) — single source of truth.
    if (window.DCG && typeof window.DCG.parseLimit === 'function') {
      return window.DCG.parseLimit(tags);
    }

    const limits = [];

    for (const raw of tags) {
      const tag = String(raw || '').trim().toLowerCase();
      let match = tag.match(/^limit-(one|two|three|four|five|six|seven|eight|nine|ten)$/);

      if (match) {
        const parsedWordLimit = WORD_TO_NUM[match[1]];
        if (Number.isFinite(parsedWordLimit) && parsedWordLimit > 0) {
          limits.push(parsedWordLimit);
        }
        continue;
      }

      match = tag.match(/^limit-(\d{1,3})$/);
      if (match) {
        const parsedNumericLimit = Number(match[1]);
        if (Number.isFinite(parsedNumericLimit) && parsedNumericLimit > 0) {
          limits.push(parsedNumericLimit);
        }
      }
    }

    if (!limits.length) return null;
    return Math.min(...limits);
  }

  function getTags(section) {
    const tagsAttr = section.getAttribute('data-dcg-tags') || '';
    return tagsAttr
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  function getMasterSelect(section) {
    return section.querySelector('select[id^="ProductSelect-"]');
  }

  function getSelectedVariantStock(section) {
    const masterSelect = getMasterSelect(section);
    if (!masterSelect) return null;

    const option = masterSelect.options[masterSelect.selectedIndex];
    if (!option) return null;

    const stockRaw = option.getAttribute('data-stock');
    if (stockRaw == null) return null;

    const stock = Number(stockRaw);
    return Number.isFinite(stock) ? stock : null;
  }

  function setDisabled(element, disabled) {
    if (!element) return;

    if (disabled) {
      element.setAttribute('disabled', 'disabled');
      element.setAttribute('aria-disabled', 'true');
    } else {
      element.removeAttribute('disabled');
      element.removeAttribute('aria-disabled');
    }
  }

  function pageAlreadyShowsInStoreMessage(section) {
    const root = section.closest('#PageContainer') || document;

    const likelyContainers = root.querySelectorAll(
      '[role="alert"], [aria-live], [class*="badge"], [class*="banner"], [class*="notice"], [class*="alert"], [class*="warning"]'
    );

    const needles = ['in-store', 'instore'];

    for (const element of likelyContainers) {
      const text = (element.textContent || '').trim().toLowerCase();
      if (text && needles.some((needle) => text.includes(needle))) {
        return true;
      }
    }

    const productArea =
      section.closest('.product-template__container') ||
      root.querySelector('.product-template__container') ||
      section;

    const fullText = (productArea.textContent || '').toLowerCase();
    return needles.some((needle) => fullText.includes(needle));
  }

  function renderQoL(section, message) {
    const qol = section.querySelector('[data-dcg-qol]');
    if (!qol) return;

    const text = String(message || '').trim();
    qol.textContent = text;
    qol.hidden = !text;
  }

  function clampQty(input, max) {
    if (!input || !Number.isFinite(max)) return;

    const current = Number(input.value || 1);
    const safeCurrent = Number.isFinite(current) && current > 0 ? current : 1;
    input.value = String(Math.min(safeCurrent, max));
  }

  function applyState(section) {
    const tags = getTags(section);
    const isRetail = tags.some((tag) => tag.toLowerCase() === 'retail');
    const limit = parseLimitFromTags(tags);

    const qtyInput = section.querySelector('input[name="quantity"].quantity');
    const btnPlus = section.querySelector('.qtyplus');
    const btnMinus = section.querySelector('.qtyminus');
    const atcBtn = section.querySelector('button[id^="AddToCart-"]');
    const dynamicCheckout = section.querySelector('#dynamic-checkout-container');

    if (isRetail) {
      setDisabled(atcBtn, true);
      setDisabled(qtyInput, true);

      if (btnPlus) {
        btnPlus.classList.add('is-disabled');
        btnPlus.style.pointerEvents = 'none';
      }

      if (btnMinus) {
        btnMinus.classList.add('is-disabled');
        btnMinus.style.pointerEvents = 'none';
      }

      if (dynamicCheckout) {
        dynamicCheckout.classList.add('hidden');
      }

      const alreadyShown = pageAlreadyShowsInStoreMessage(section);
      renderQoL(section, alreadyShown ? '' : 'In-store only. Call for details.');
      return;
    }

    setDisabled(qtyInput, false);

    if (btnMinus) {
      btnMinus.classList.remove('is-disabled');
      btnMinus.style.pointerEvents = '';
      btnMinus.style.opacity = '';
    }

    let maxAllowed = Infinity;
    const stock = getSelectedVariantStock(section);

    if (Number.isFinite(stock)) {
      maxAllowed = Math.max(0, stock);
    }

    if (typeof limit === 'number' && limit > 0) {
      maxAllowed = Math.min(maxAllowed, limit);
    }

    if (!Number.isFinite(maxAllowed)) {
      maxAllowed = typeof limit === 'number' && limit > 0 ? limit : Infinity;
    }

    if (qtyInput && Number.isFinite(maxAllowed) && maxAllowed !== Infinity) {
      const minPurchasable = maxAllowed > 0 ? 1 : 0;
      if (Number(qtyInput.value) < minPurchasable) {
        qtyInput.value = String(minPurchasable);
      }
      clampQty(qtyInput, maxAllowed);
    }

    const messages = [];
    if (typeof limit === 'number' && limit > 0) {
      messages.push(`Limit ${limit} per customer`);
    }
    renderQoL(section, messages.join(' • '));

    if (btnPlus && qtyInput && Number.isFinite(maxAllowed) && maxAllowed !== Infinity) {
      const qty = Number(qtyInput.value || 1);
      const atMax = qty >= maxAllowed;
      btnPlus.classList.toggle('is-disabled', atMax);
      btnPlus.style.pointerEvents = atMax ? 'none' : '';
      btnPlus.style.opacity = atMax ? '0.45' : '';
    } else if (btnPlus) {
      btnPlus.classList.remove('is-disabled');
      btnPlus.style.pointerEvents = '';
      btnPlus.style.opacity = '';
    }

    if (atcBtn) {
      const disableCart = Number.isFinite(maxAllowed) && maxAllowed <= 0;
      setDisabled(atcBtn, disableCart);
    }

    if (dynamicCheckout) {
      const disableDynamicCheckout = Number.isFinite(maxAllowed) && maxAllowed <= 0;
      dynamicCheckout.classList.toggle('hidden', disableDynamicCheckout);
    }

    // Keep the hidden input shop.js reads for its ATC call in sync with the
    // visible quantity the customer has actually selected. shop.js reads
    // input[name="product-quantity"] (hardcoded value="1") while the real qty
    // lives in input[name="quantity"].quantity — they are two separate inputs
    // and the hidden one is never updated by the +/- handlers in shop.js.
    const hiddenQtyInput = section.querySelector('input[name="product-quantity"]');
    if (hiddenQtyInput && qtyInput) {
      hiddenQtyInput.value = qtyInput.value;
    }
  }

  function bindQtyGuard(section) {
    if (!section || section.dataset[INIT_FLAG] === 'true') return;

    const qtyInput = section.querySelector('input[name="quantity"].quantity');
    const masterSelect = getMasterSelect(section);

    section.addEventListener('click', (event) => {
      if (event.target.closest('.qtyplus') || event.target.closest('.qtyminus')) {
        setTimeout(() => applyState(section), 0);
      }
    });

    if (qtyInput && qtyInput.dataset.dcgQtyInputBound !== 'true') {
      qtyInput.dataset.dcgQtyInputBound = 'true';
      qtyInput.addEventListener('change', () => applyState(section));
      qtyInput.addEventListener('input', () => applyState(section));
    }

    if (masterSelect && masterSelect.dataset.dcgQtyMasterBound !== 'true') {
      masterSelect.dataset.dcgQtyMasterBound = 'true';
      masterSelect.addEventListener('change', () => applyState(section));
    }

    // DCG V3.1.1 (hotfix.1, H9 of the engineering review): a pick of another condition. theme.js (slate.Variants,
    // never edited: D-SP-02) writes the pick to the master select with jQuery's .val(), which fires no `change`, so the
    // listener above never heard a pick: the cap stayed the opening condition's ("+" greyed on 1 in stock after picking
    // a condition with more). A pick is a `change` on another select of this section (the DCG condition select, the
    // theme's option selectors), and the theme's handlers write the master select inside that event: the state is
    // applied again a moment after it, from the master select as they left it.
    if (section.dataset.dcgQtyPickBound !== 'true') {
      section.dataset.dcgQtyPickBound = 'true';
      section.addEventListener('change', (event) => {
        const picked = event.target;
        if (!picked || picked === getMasterSelect(section) || !picked.matches || !picked.matches('select')) return;
        setTimeout(() => applyState(section), 0);
      });
    }

    applyState(section);
    section.dataset[INIT_FLAG] = 'true';
  }

  function initAll(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach(bindQtyGuard);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.(SECTION_SELECTOR)
      ? event.target
      : event.target.querySelector?.(SECTION_SELECTOR);

    if (section) {
      section.dataset[INIT_FLAG] = 'false';
      bindQtyGuard(section);
    }
  });

})();